<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { ArrowLeft, ArrowRight, Search, UserRound } from 'lucide-vue-next'
import { hasRole } from '@ck/auth'
import { customersApi } from '@ck/api-client'
import { maskPii, totalPages } from '../lib/operational'
import { CkAlert, CkButton, CkField, CkTable } from '@ck/ui'

const pageSize = 20
const route = useRoute()
const router = useRouter()
const canViewPii = computed(() => hasRole('admin') || hasRole('manager'))
const search = ref(typeof route.query.q === 'string' ? route.query.q : '')
const q = computed(() => typeof route.query.q === 'string' ? route.query.q : '')
const page = computed(() => {
  const value = Number(route.query.page ?? 1)
  return Number.isInteger(value) && value > 0 ? value : 1
})

watch(() => route.query.q, (value) => { search.value = typeof value === 'string' ? value : '' })

const customersQuery = useQuery(computed(() => ({
  queryKey: ['customers', q.value, page.value, pageSize],
  queryFn: () => customersApi.list({ q: q.value || undefined, page: page.value, pageSize })
})))
const result = computed(() => customersQuery.data.value)
const pages = computed(() => totalPages(result.value?.total ?? 0, pageSize))

function applySearch() {
  router.replace({ query: { ...route.query, q: search.value.trim() || undefined, page: undefined } })
}

function goToPage(nextPage: number) {
  if (nextPage < 1 || nextPage > pages.value) return
  router.replace({ query: { ...route.query, page: nextPage === 1 ? undefined : String(nextPage) } })
}

function openCustomer(id: string) {
  router.push({ name: 'cliente-detalhe', params: { id } })
}

function formatPii(value: string | null) {
  return canViewPii.value ? value || '—' : maskPii(value)
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <div>
        <span class="ck-eyebrow">CADASTRO</span>
        <h1>Clientes</h1>
        <p>Clientes, veículos e histórico de ordens de serviço.</p>
      </div>
    </div>

    <section class="filters" aria-label="Filtros de clientes">
      <form class="search-form" @submit.prevent="applySearch">
        <CkField
          v-model="search"
          label="Buscar cliente"
          placeholder="Nome ou e-mail"
          inputmode="search"
          autocomplete="off"
          :maxlength="120"
          :disabled="customersQuery.isFetching.value"
        />
        <CkButton type="submit" variant="secondary" :disabled="customersQuery.isFetching.value">
          <Search :size="16" aria-hidden="true"/>Buscar
        </CkButton>
      </form>
      <p class="result-count" aria-live="polite">{{ result?.total ?? 0 }} clientes encontrados</p>
    </section>

    <CkAlert v-if="customersQuery.isError.value" tone="error">Não foi possível carregar os clientes.</CkAlert>
    <div v-else-if="customersQuery.isPending.value" class="status-message" role="status">Carregando clientes…</div>
    <div v-else class="table-wrap">
      <CkTable
        :columns="[
          { key: 'name', label: 'Cliente', width: '1.4fr' },
          { key: 'email', label: 'E-mail', width: '1.2fr' },
          { key: 'phone', label: 'Telefone', width: '1fr' },
          { key: 'document', label: 'CPF/CNPJ', width: '1fr' },
          { key: 'open', label: '', width: '70px', align: 'right' }
        ]"
        :rows="result?.items ?? []"
        :row-key="(row) => row.id"
        empty-message="Nenhum cliente encontrado."
      >
        <template #default="{ item }">
          <button type="button" class="customer-cell" @click="openCustomer(item.id)">
            <span class="avatar" aria-hidden="true"><UserRound :size="16"/></span>
            <span><strong>{{ item.name }}</strong><small class="ck-code">{{ item.id.slice(0, 8) }}</small></span>
          </button>
          <button type="button" class="link-cell" @click="openCustomer(item.id)">{{ item.email }}</button>
          <button type="button" class="link-cell" @click="openCustomer(item.id)">{{ formatPii(item.phone) }}</button>
          <button type="button" class="link-cell mono" @click="openCustomer(item.id)">{{ formatPii(item.cpfCnpj) }}</button>
          <button type="button" class="open-cell" :aria-label="`Abrir cliente ${item.name}`" @click="openCustomer(item.id)">
            <ArrowRight :size="17" aria-hidden="true"/>
          </button>
        </template>
      </CkTable>
    </div>

    <nav v-if="pages > 1" class="pagination" aria-label="Paginação de clientes">
      <CkButton variant="ghost" :disabled="page <= 1" aria-label="Página anterior" @click="goToPage(page - 1)">
        <ArrowLeft :size="16" aria-hidden="true"/>Anterior
      </CkButton>
      <span>Página <strong>{{ page }}</strong> de <strong>{{ pages }}</strong></span>
      <CkButton variant="ghost" :disabled="page >= pages" aria-label="Próxima página" @click="goToPage(page + 1)">
        Próxima<ArrowRight :size="16" aria-hidden="true"/>
      </CkButton>
    </nav>
  </div>
</template>

<style scoped>
.page-head { margin-bottom: var(--space-5); }
.ck-eyebrow { color: var(--ck-amber-400); }
.page-head h1 { margin: 5px 0 4px; font-size: var(--fs-2xl); font-weight: var(--fw-extrabold); }
.page-head p { margin: 0; color: var(--color-text-muted); font-size: var(--fs-sm); }
.filters { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--space-5); margin-bottom: var(--space-4); padding: var(--space-4); border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.search-form { display: flex; align-items: flex-end; gap: var(--space-3); width: min(620px, 100%); }
.search-form :deep(.ck-field) { flex: 1; margin-bottom: 0; }
.result-count { margin: 0 0 12px; color: var(--color-text-faint); font-size: var(--fs-xs); white-space: nowrap; }
.table-wrap { overflow-x: auto; }
.table-wrap :deep(.ck-table) { min-width: 780px; }
.customer-cell, .link-cell, .open-cell { display: flex; align-items: center; border: 0; background: transparent; color: var(--color-text); font: inherit; text-align: left; cursor: pointer; }
.customer-cell { gap: var(--space-3); }
.customer-cell strong { display: block; font-size: var(--fs-sm); }
.customer-cell small { display: block; margin-top: 2px; color: var(--color-text-faint); font-size: var(--fs-2xs); }
.avatar { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: var(--radius-full); background: var(--color-surface-muted); color: var(--ck-amber-400); }
.link-cell { width: 100%; color: var(--color-text-muted); overflow-wrap: anywhere; }
.link-cell:hover, .customer-cell:hover { color: var(--ck-amber-400); }
.mono { font-family: var(--font-mono); font-size: var(--fs-xs); }
.open-cell { justify-content: flex-end; width: 100%; color: var(--color-text-faint); }
.open-cell:hover { color: var(--ck-orange-500); }
.pagination { display: flex; align-items: center; justify-content: center; gap: var(--space-4); margin-top: var(--space-5); color: var(--color-text-muted); font-size: var(--fs-sm); }
.pagination :deep(.ck-button) { min-height: 38px; padding: 8px 12px; }
.status-message { padding: var(--space-6); color: var(--color-text-muted); }
@media (max-width: 700px) {
  .filters { align-items: stretch; flex-direction: column; gap: var(--space-2); }
  .search-form { width: 100%; }
  .result-count { margin: 0; }
}
@media (max-width: 420px) {
  .search-form { align-items: stretch; flex-direction: column; }
  .search-form :deep(.ck-field) { width: 100%; }
  .search-form :deep(.ck-button) { width: 100%; }
  .pagination { gap: var(--space-2); justify-content: space-between; }
  .pagination span { font-size: var(--fs-xs); }
}
</style>
